import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { CheckCircleIcon, XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface SubscriptionPlan {
  id: string;
  name: string;
  duration_months: number;
  price: number;
  currency: string;
  features: string[];
  popular?: boolean;
  recommended?: boolean;
}

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  plans: SubscriptionPlan[];
  message?: string;
  trialDaysRemaining?: number;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  plans,
  message,
  trialDaysRemaining
}) => {
  const navigate = useNavigate();

  const handlePlanSelect = (planId: string) => {
    // Navigate to payment page with selected plan
    navigate(`/payment?plan=${planId}`);
  };

  const getPlanBadge = (plan: SubscriptionPlan) => {
    if (plan.recommended) {
      return (
        <span className="absolute top-0 right-0 transform translate-x-2 -translate-y-1/2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs px-3 py-1 rounded-full font-semibold shadow-lg">
          BEST VALUE
        </span>
      );
    }
    if (plan.popular) {
      return (
        <span className="absolute top-0 right-0 transform translate-x-2 -translate-y-1/2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xs px-3 py-1 rounded-full font-semibold shadow-lg">
          POPULAR
        </span>
      );
    }
    return null;
  };

  const getSavingsAmount = (plan: SubscriptionPlan) => {
    const monthlyRate = 100; // Base monthly rate for comparison
    const fullPrice = monthlyRate * plan.duration_months;
    const savings = fullPrice - plan.price;
    return savings > 0 ? savings : 0;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-8 text-left align-middle shadow-2xl transition-all">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>

                <Dialog.Title
                  as="h3"
                  className="text-3xl font-bold text-gray-900 text-center mb-4"
                >
                  <div className="flex items-center justify-center gap-3">
                    <SparklesIcon className="h-8 w-8 text-purple-600" />
                    Unlock Full Access to Knoozy
                    <SparklesIcon className="h-8 w-8 text-purple-600" />
                  </div>
                </Dialog.Title>

                {message && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 text-center">
                    <p className="text-yellow-800 font-medium">{message}</p>
                    {trialDaysRemaining !== undefined && trialDaysRemaining > 0 && (
                      <p className="text-yellow-600 text-sm mt-1">
                        {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining in your free trial
                      </p>
                    )}
                  </div>
                )}

                <p className="text-gray-600 text-center mb-8">
                  Choose the perfect plan for your learning journey. All plans include unlimited access to age-appropriate articles, daily quizzes, and progress tracking.
                </p>

                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  {plans.map((plan) => (
                    <div
                      key={plan.id}
                      className={`relative rounded-2xl bg-white p-6 shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                        plan.recommended ? 'ring-2 ring-purple-500 ring-offset-2' : ''
                      }`}
                    >
                      {getPlanBadge(plan)}

                      <div className="mb-6">
                        <h4 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h4>
                        <div className="flex items-baseline">
                          <span className="text-4xl font-bold text-gray-900">₹{plan.price}</span>
                          <span className="text-gray-600 ml-2">/ {plan.duration_months} months</span>
                        </div>
                        {getSavingsAmount(plan) > 0 && (
                          <div className="mt-2 inline-block bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full font-semibold">
                            Save ₹{getSavingsAmount(plan)}
                          </div>
                        )}
                      </div>

                      <ul className="space-y-3 mb-6">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700 text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => handlePlanSelect(plan.id)}
                        className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-200 ${
                          plan.recommended
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-lg'
                            : plan.popular
                            ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:from-blue-700 hover:to-cyan-700'
                            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                        }`}
                      >
                        Choose {plan.name}
                      </button>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-4 text-center">
                  <p className="text-gray-700 text-sm">
                    <strong>100% Secure Payment</strong> • Cancel anytime • 24/7 Support
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SubscriptionModal;